import React, { useState, useEffect } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { SignalsPreview } from './SignalsPreview';
import { TrendingUp, ShieldAlert, History, BarChart3, Send } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

interface Props {
    session: Session;
}

const Elite53Signals: React.FC<Props> = ({ session }) => {
    const navigate = useNavigate();
    const [hasSignalAccess, setHasSignalAccess] = useState(false);
    const [userTier, setUserTier] = useState('Free');
    const [loading, setLoading] = useState(true);

    // Live stats for elite_53 only
    const [liveStats, setLiveStats] = useState({
        total: 0, closed: 0, active: 0, netRR: 0,
    });

    const fetchLiveStats = async () => {
        const { count: activeCount } = await supabase
            .from('signals')
            .select('*', { count: 'exact', head: true })
            .eq('scanner_type', 'elite_53')
            .in('result', ['active', 'pending']);

        const { data: closedData } = await supabase
            .from('signals')
            .select('result, status, rr_ratio, rr_at_tp1')
            .eq('scanner_type', 'elite_53')
            .in('result', ['win', 'loss', 'invalid']);

        if (closedData) {
            let netRR = 0;
            closedData.forEach(s => {
                if (s.result === 'win' && s.status === 'be_closed') {
                    netRR += typeof s.rr_at_tp1 === 'number' ? s.rr_at_tp1 : 0;
                } else if (s.result === 'win') {
                    netRR += typeof s.rr_ratio === 'number' ? s.rr_ratio : 0;
                } else if (s.result === 'loss') {
                    netRR -= 1;
                }
            });
            setLiveStats({
                total: (activeCount || 0) + closedData.length,
                closed: closedData.length,
                active: activeCount || 0,
                netRR: parseFloat(netRR.toFixed(2)),
            });
        }
    };

    useEffect(() => {
        const checkAccess = async () => {
            if (!session?.user?.id) { setLoading(false); return; }
            const { data: profile } = await supabase
                .from('user_profiles')
                .select('tier, subscription_status, plan_expires_at')
                .eq('id', session.user.id)
                .single();

            if (profile) {
                const notExpired = !profile.plan_expires_at || new Date(profile.plan_expires_at) > new Date();
                const statusOk = !profile.subscription_status || ['active', 'trial'].includes(profile.subscription_status);
                setHasSignalAccess(['Signals', 'Premium', 'Elite'].includes(profile.tier) && notExpired && statusOk);
                setUserTier(profile.tier || 'Free');
            }
            setLoading(false);
        };
        checkAccess();
        fetchLiveStats();
    }, [session]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-finance-darker">
                <div className="w-8 h-8 border-4 border-finance-accent border-r-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <main className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">

            {/* Header */}
            <div className="mb-14 border-b border-white/5 pb-12 rounded-b-[4rem] bg-[#000000] text-center relative overflow-hidden shadow-2xl">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(#7c3aed 0.5px, transparent 0.5px)', backgroundSize: '24px 24px' }} />
                <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[900px] h-[350px] bg-purple-600/10 blur-[130px] rounded-full pointer-events-none" />

                <div className="relative z-10 inline-flex items-center justify-center gap-3 px-6 py-2 rounded-full border border-purple-500/30 bg-purple-500/5 text-purple-400 text-[11px] font-black tracking-[0.4em] uppercase mb-8 shadow-inner">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-400" />
                    </span>
                    ICT/SMC ELITE 53 STRATEGIES
                </div>

                <h1 className="text-5xl md:text-7xl font-black text-white mb-5 tracking-tighter relative z-10">
                    Elite <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-300 to-purple-400">Precision</span>
                </h1>
                <p className="text-gray-400 max-w-2xl mx-auto text-lg font-medium leading-relaxed px-4 opacity-80 relative z-10">
                    Backtested 53-strategy scanner. Only signals with
                    <span className="text-white"> Sharpe ≥ 2.0 · PF ≥ 1.3 · 20+ trades</span> in backtest.
                </p>

                <div className="flex items-center justify-center gap-3 mt-5 flex-wrap">
                    {[
                        { label: 'Strategies',   value: '53', color: 'text-purple-400' },
                        { label: 'Min Sharpe',   value: '≥ 2.0', color: 'text-pink-400' },
                        { label: 'Min PF',       value: '≥ 1.3', color: 'text-amber-400' },
                        { label: 'Timeframe',    value: 'XAU/USD 15M', color: 'text-white' },
                    ].map(({ label, value, color }) => (
                        <div key={label} className="px-4 py-1.5 rounded-full bg-gray-900 border border-gray-800 text-center">
                            <span className="text-gray-500 text-[10px] uppercase tracking-widest mr-2">{label}:</span>
                            <span className={`${color} text-xs font-black`}>{value}</span>
                        </div>
                    ))}
                </div>
            </div>

            {!hasSignalAccess ? (
                /* ── Access Gate ── */
                <div className="relative isolate px-6 py-16 sm:py-24 lg:px-8 bg-[#000000] rounded-3xl border border-purple-500/20 text-center overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-pink-900/5 pointer-events-none" />
                    <div className="w-24 h-24 mx-auto mb-6 bg-finance-dark rounded-full border border-purple-500/30 flex items-center justify-center shadow-xl relative z-10">
                        <BarChart3 className="h-10 w-10 text-purple-400" />
                    </div>
                    <h2 className="text-3xl font-black text-white mb-4 relative z-10">Elite Access Required</h2>
                    <p className="max-w-xl mx-auto text-gray-400 mb-8 relative z-10">
                        Elite 53 Signals are available for <span className="text-white font-semibold">Premium</span> and <span className="text-purple-400 font-semibold">Elite</span> subscribers.
                    </p>
                    <button
                        onClick={() => navigate('/pricing')}
                        className="bg-purple-600 text-white uppercase font-black tracking-widest px-10 py-4 rounded-xl shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:scale-105 transition-transform"
                    >
                        Upgrade Plan
                    </button>
                </div>
            ) : (
                <div className="space-y-10">
                    {/* Live Stats Bar — Elite 53 only */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { label: 'Total Signals', value: liveStats.total, color: 'text-white' },
                            { label: 'Active Now',    value: liveStats.active, color: 'text-purple-400' },
                            { label: 'Closed Trades', value: liveStats.closed, color: 'text-blue-400' },
                            { label: 'Net R:R', value: `${liveStats.netRR >= 0 ? '+' : ''}${liveStats.netRR}R`, color: liveStats.netRR >= 0 ? 'text-emerald-400' : 'text-rose-400' },
                        ].map(({ label, value, color }) => (
                            <div key={label} className="bg-gray-900/60 border border-gray-800 rounded-xl p-4 text-center">
                                <p className="text-gray-500 text-xs uppercase tracking-widest mb-1">{label}</p>
                                <p className={`${color} font-black text-xl font-mono`}>{value}</p>
                            </div>
                        ))}
                    </div>

                    {/* Live Signals — elite_53 scanner only */}
                    <section>
                        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                            <h2 className="text-2xl font-black text-white flex items-center gap-3">
                                <span className="relative flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-400" />
                                </span>
                                Active Elite Signals
                            </h2>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => navigate('/signal-history?scanner=elite_53')}
                                    className="text-gray-400 text-sm font-bold uppercase tracking-wider hover:text-white flex items-center gap-1.5 bg-gray-800 px-4 py-2 rounded-lg border border-gray-700 hover:bg-gray-700 transition-colors"
                                >
                                    <History className="h-4 w-4" />
                                    Elite History
                                </button>
                                <button
                                    onClick={() => navigate('/signals')}
                                    className="text-purple-400 text-sm font-bold uppercase tracking-wider hover:text-purple-300 flex items-center gap-1 bg-purple-500/5 px-4 py-2 rounded-lg border border-purple-500/10 transition-colors"
                                >
                                    ICT Signals →
                                </button>
                            </div>
                        </div>
                        {/* Telegram Join Banner */}
                        <a
                            href="https://t.me/forexfundai"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mb-6 flex items-center justify-between gap-4 bg-[#229ED9]/10 border border-[#229ED9]/30 rounded-2xl p-5 hover:bg-[#229ED9]/15 hover:border-[#229ED9]/50 transition-all group"
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-[#229ED9]/20 flex items-center justify-center shrink-0">
                                    <Send className="w-6 h-6 text-[#229ED9]" />
                                </div>
                                <div>
                                    <p className="text-white font-black text-sm">Get Elite Signals on Telegram — Free</p>
                                    <p className="text-gray-400 text-xs mt-0.5">All elite signals are auto-posted to our channel with full Entry, SL &amp; TP the moment they fire.</p>
                                </div>
                            </div>
                            <div className="shrink-0 px-4 py-2 rounded-xl bg-[#229ED9] text-white text-xs font-black uppercase tracking-widest group-hover:brightness-110 transition-all">
                                Join Free →
                            </div>
                        </a>
                        <SignalsPreview scannerType="elite_53" isPremium={true} limit={12} />
                    </section>
                </div>
            )}

            {/* Risk Disclosure */}
            <div className="mt-20 pt-8 border-t border-gray-800/50">
                <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
                    <div className="bg-red-500/10 p-4 rounded-xl">
                        <ShieldAlert className="w-8 h-8 text-red-500" />
                    </div>
                    <div>
                        <h4 className="text-white font-black uppercase tracking-tight mb-1 italic">Mandatory Risk Warning</h4>
                        <p className="text-gray-400 text-sm leading-relaxed max-w-4xl">
                            Backtested strategies do not guarantee future performance. These signals are for <strong>informational and educational purposes only</strong> and do NOT constitute financial advice.
                            ForexFund AI accepts no liability for any trading losses.
                        </p>
                    </div>
                    <div className="md:ml-auto">
                        <Link to="/disclaimer" className="text-finance-accent text-xs font-black uppercase tracking-widest hover:underline whitespace-nowrap">
                            Read Full Disclosure
                        </Link>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default Elite53Signals;
